import { ArrowLeft02Icon, Cancel01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import { useSearchProfiles } from '@sunrise-ui/api-client';
import { ProfileResult } from '@sunrise-ui/composites';
import {
  Colors,
  Loader,
  TextInput,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { TextInput as PaperTextInput } from 'react-native-paper';
import useBlockedUsers from '../../../contexts/useBlockedUsers';

const CancelIcon = () => (
  <HugeiconsIcon icon={Cancel01Icon} color={Colors.text} />
);

type Props = {
  onClose?: () => void;
  autoFocus?: boolean;
  search?: string;
  onSearchChange?: (value: string) => void;
  showInput?: boolean;
};

export function PersonSearchDropdown({
  onClose,
  autoFocus = false,
  search: externalSearch,
  onSearchChange,
  showInput = true,
}: Props) {
  const [internalSearch, setInternalSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { height } = useWindowDimensions();
  const { isMobile } = useMediaQueries();
  const styles = useDynamicStyles(createStyles, { screenHeight: height });

  const { currentUserId } = useAuthStore();
  const { blockedUserIds } = useBlockedUsers();

  // Use external search if provided, otherwise use internal state
  const search = externalSearch !== undefined ? externalSearch : internalSearch;
  const handleSearchChange =
    onSearchChange !== undefined ? onSearchChange : setInternalSearch;

  const minChars = 3;

  // Debounce logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useSearchProfiles(debouncedSearch);

  const profiles = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page?.results ?? [])
        .filter(
          (profile) =>
            profile.user_id !== currentUserId &&
            !blockedUserIds.has(profile.user_id),
        ) ?? [],
    [data, currentUserId, blockedUserIds],
  );

  const hasRelevantData = useMemo(
    () =>
      data &&
      data?.pages[0]?.searchQuery.slice(0, minChars) ===
        search.slice(0, minChars),
    [data, search, minChars],
  );

  const handleSelect = useCallback(
    (userId: string) => {
      handleSearchChange('');
      onClose?.();
      router.navigate(`/to/${userId}`);
    },
    [handleSearchChange, onClose],
  );

  const handleClear = useCallback(() => {
    handleSearchChange('');
  }, [handleSearchChange]);

  const handleBack = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleEndReached = useCallback(async () => {
    if (hasNextPage && !isFetching) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  return (
    <View style={showInput ? styles.container : styles.containerNoInput}>
      {showInput && (
        <View style={styles.searchRow}>
          {/* Back button - LinkedIn style */}
          {isMobile && (
            <Pressable onPress={handleBack} style={styles.backButton}>
              <HugeiconsIcon
                icon={ArrowLeft02Icon}
                color={Colors.text}
                size={24}
              />
            </Pressable>
          )}
          <View style={styles.inputWrapper}>
            <TextInput
              label="Search for someone..."
              showLabel={false}
              value={search}
              onChange={handleSearchChange}
              style={styles.searchInput}
              autoCorrect={false}
              dense={true}
              autoFocus={autoFocus}
              right={
                search.length > 0 ? (
                  <PaperTextInput.Icon
                    icon={CancelIcon}
                    onPress={handleClear}
                  />
                ) : undefined
              }
            />
          </View>
        </View>
      )}
      <FlatList
        data={hasRelevantData ? profiles : []}
        bounces={false}
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        renderItem={({ item: profile }) => (
          <TouchableOpacity
            key={profile.user_id}
            style={styles.listItem}
            onPress={() => handleSelect(profile.user_id)}
          >
            <ProfileResult
              userId={profile.user_id}
              size="small"
              isOneLineText={false}
            />
          </TouchableOpacity>
        )}
        keyExtractor={(item) => item.user_id}
        onEndReached={handleEndReached}
        windowSize={4}
        initialNumToRender={5}
        ListFooterComponent={isFetchingNextPage ? <Loader /> : null}
        contentContainerStyle={styles.listContent}
        style={styles.list}
        ListEmptyComponent={
          search ? (
            <View style={styles.emptyListItem}>
              {search.length > minChars - 1 ? (
                hasRelevantData ? (
                  <TextReg>No results found</TextReg>
                ) : (
                  <Loader />
                )
              ) : (
                <TextReg>
                  {`Please enter ${minChars} or more characters to search`}
                </TextReg>
              )}
            </View>
          ) : null
        }
      />
    </View>
  );
}

const createStyles = ({
  isMobile,
  screenHeight,
}: WithResponsive<{ screenHeight: number }>) =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
      gap: 12,
    },
    containerNoInput: {
      width: '100%',
      maxHeight: screenHeight ? screenHeight * 0.6 : 500,
    },
    searchRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    backButton: {
      padding: 8,
      marginLeft: -8,
    },
    inputWrapper: {
      flex: 1,
    },
    searchInput: {
      backgroundColor: Colors.opacity05,
      borderRadius: 24,
    },
    list: {
      flex: 1,
    },
    listContent: {
      paddingBottom: 8,
    },
    listItem: {
      display: 'flex',
      flexDirection: 'row',
      gap: 20,
      alignItems: 'center',
      marginBottom: 12,
      paddingHorizontal: isMobile ? 4 : 8,
    },
    emptyListItem: {
      padding: 16,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
